import React, { useState, useEffect, useCallback } from 'react';
import {
   Container,
   Paper,
   TableContainer,
   Snackbar,
   Alert,
   Tabs,
   Tab,
   Box
} from '@mui/material';
import styles from './admin.module.css';
import {
   fetchAllUserProfiles,
   fetchUserProfile,
   updateUserProfile,
   addNewUser,
   deleteUserProfile,
   hardDeleteUserProfile,
   toggleBlockUser,
   exportUsers,
   createPermissionHandler,
   deleteUserProfileField,
   resetUserBirthdayTimer
} from './admin.functions';
import AdminService from '../../services/admin.services';
import logger from '../../utils/logger';
import AddUserModal from './AddUserModal';
import EditUserModal from './EditUserModal';
import ConfirmationDialogs from './ConfirmationDialogs';
import AdminHeader from './AdminHeader';
import UserManagementTable from './UserManagementTable';
import PermissionGroups from './PermissionGroups';
import PAGINATION_CONFIG, { PaginationUtils } from '../../config/paginationConfig';

const Admin = (props) => {
   const [selectedTab, setSelectedTab] = useState(() => {
      // Initialize from localStorage or default to 0 (users tab)
      const savedTab = localStorage.getItem('adminSelectedTab');
      return savedTab ? parseInt(savedTab, 10) : 0;
   });

   const handleTabChange = (event, newValue) => {
      setSelectedTab(newValue);
      localStorage.setItem('adminSelectedTab', newValue.toString());
   };

   const [userInput, setUserInput] = useState('');
   const [openGetUserModal, setGetUserOpen] = useState(false);
   const [openAddUserModal, setAddUserOpen] = useState(false);
   const [profileData, setProfileData] = useState({});
   const [groups, setGroups] = useState([]);
   const [GSDgroups, setGSDGroups] = useState([]);
   const [groupsPayload, setGroupsPayload] = useState([]);

   // For Add User
   const [permission, setPermission] = useState([]);
   const [sendgridPermission, setSendgridPermission] = useState([]);
   const [mediaRelationsPermission, setMediaRelationsPermission] = useState([]);
   const [companiesPermission, setCompaniesPermission] = useState([]);
   const [dashboardPermission, setDashboardPermission] = useState([]);
   const [twitterXPermission, setTwitterXPermission] = useState([]);
   const [entitlementPermission, setEntitlementPermission] = useState([]);
   const [intercomPermission, setIntercomPermission] = useState([]);
   const [toolsPermission, setToolsPermission] = useState([]);
   const [auth0Permission, setAuth0Permission] = useState([]);
   const [scriptRunnerv2Permission, setScriptRunnerv2Permission] = useState([]);

   // For Edit User
   const [editPermission, setEditPermission] = useState([]);
   const [editSendgridPermission, setEditSendgridPermission] = useState([]);
   const [editMediaRelationsPermission, setEditMediaRelationsPermission] = useState([]);
   const [editCompaniesPermission, setEditCompaniesPermission] = useState([]);
   const [editDashboardPermission, setEditDashboardPermission] = useState([]);
   const [editTwitterXPermission, setEditTwitterXPermission] = useState([]);
   const [editEntitlementPermission, setEditEntitlementPermission] = useState([]);
   const [editIntercomPermission, setEditIntercomPermission] = useState([]);
   const [editToolsPermission, setEditToolsPermission] = useState([]);
   const [editAuth0Permission, setEditAuth0Permission] = useState([]);
   const [editScriptRunnerv2Permission, setEditScriptRunnerv2Permission] = useState([]);

   // Add state for delete options
   const [deleteOptionsOpen, setDeleteOptionsOpen] = useState(false);
   const [deleteType, setDeleteType] = useState("soft");
   const [userToDeleteWithOptions, setUserToDeleteWithOptions] = useState(null);

   const [active, setActive] = useState(false);
   const [openSnackBar, setOpenSnackBar] = useState(false);
   const [feedbackMsg, setFeedbackMsg] = useState('');
   const [severity, setSeverity] = useState('info');
   const [isLoading, setIsLoading] = useState(false);
   const [page, setPage] = useState(PAGINATION_CONFIG.DEFAULT_PAGE);
   const [rowsPerPage, setRowsPerPage] = useState(PAGINATION_CONFIG.DEFAULT_PAGE_SIZE);
   const [searchedVal, setSearchedVal] = useState('');
   const [users, setUsers] = useState([]);
   const [totalUsers, setTotalUsers] = useState(0);
   const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

   // Inline debounce logic for search
   const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

   useEffect(() => {
      const timer = setTimeout(() => {
         setDebouncedSearchTerm(searchedVal);
      }, PAGINATION_CONFIG.SEARCH_DEBOUNCE_DELAY);

      return () => {
         clearTimeout(timer);
      };
   }, [searchedVal]);

   const [userToDelete, setUserToDelete] = useState(null);
   const [blockConfirmOpen, setBlockConfirmOpen] = useState(false);
   const [userToToggleBlock, setUserToToggleBlock] = useState(null);
   const [blockAction, setBlockAction] = useState(false);

   // State for profile data deletion
   const [deleteFieldConfirmOpen, setDeleteFieldConfirmOpen] = useState(false);
   const [fieldToDelete, setFieldToDelete] = useState({ email: '', field: '', label: '' });
   const [deleteProfileFieldLoading, setDeleteProfileFieldLoading] = useState(false);
   const [resetBirthdayTimer, setResetBirthdayTimer] = useState(false);
   const [resetBirthdayTimerConfirmOpen, setResetBirthdayTimerConfirmOpen] = useState(false);
   const [userForBirthdayTimerReset, setUserForBirthdayTimerReset] = useState('');
   const [resetBirthdayTimerLoading, setResetBirthdayTimerLoading] = useState(false);

   // Sort functionality - handled server-side
   const [order, setOrder] = useState('asc');
   const [orderBy, setOrderBy] = useState('email');

   const handleRequestSort = (property) => {
      // Don't sort by action column
      if (property === 'actions') return;

      const isAsc = orderBy === property && order === 'asc';
      const newOrder = isAsc ? 'desc' : 'asc';
      setOrder(newOrder);
      setOrderBy(property);

      // Reset to first page when changing sort order
      setPage(0);
   };

   // Handle blocking user with confirmation dialog
   const handleBlockConfirmOpen = (email, block) => {
      setUserToToggleBlock(email);
      setBlockAction(block);
      setBlockConfirmOpen(true);
   };

   const handleBlockConfirmed = async () => {
      try {
         setBlockConfirmOpen(false);
         setIsLoading(true);

         const result = await toggleBlockUser(userToToggleBlock, blockAction);
         if (result.success) {
            // Refresh profile data from server to get latest state
            await refreshProfileData(userToToggleBlock);

            setOpenSnackBar(true);
            setSeverity('success');
            setFeedbackMsg(result.message);
         } else {
            setOpenSnackBar(true);
            setFeedbackMsg(result.error);
            setSeverity('error');
         }
      } finally {
         setIsLoading(false);
      }
   };

   // Pagination Functions
   const handleChangePage = (event, newPage) => {
      const safePage = PaginationUtils.getSafePage(newPage, totalUsers, rowsPerPage);
      if (safePage !== page) {
         setPage(safePage);
      }
   };

   const handleChangeRowsPerPage = (event) => {
      const requestedPageSize = +event.target.value;
      const validPageSize = PaginationUtils.getValidPageSize(requestedPageSize);

      setRowsPerPage(validPageSize);
      const safePage = PaginationUtils.getSafePage(0, totalUsers, validPageSize);
      setPage(safePage);
   };

   // Handle search input with debounce
   const handleSearchChange = useCallback((searchValue) => {
      // Only reset page if search value actually changed
      if (searchedVal !== searchValue) {
         setSearchedVal(searchValue);
         // Reset to first page but ensure it's safe
         setPage(PAGINATION_CONFIG.DEFAULT_PAGE);
      } else {
         // Still update the search value to maintain consistency
         setSearchedVal(searchValue);
      }
   }, [searchedVal]);

   // Modal Functions
   const handleGetUserModalClose = () => {
      setGetUserOpen(false);
      setProfileData({});
      setGroups([]);
      setGroupsPayload([]);
      // Reset edit permissions
      setEditPermission([]);
      setEditSendgridPermission([]);
      setEditMediaRelationsPermission([]);
      setEditCompaniesPermission([]);
      setEditDashboardPermission([]);
      setEditTwitterXPermission([]);
      setEditEntitlementPermission([]);
      setEditIntercomPermission([]);
      setEditToolsPermission([]);
      setEditAuth0Permission([]);
      setEditScriptRunnerv2Permission([]);
      if (active === true) {
         setActive(false);
      }
   }

   const handleAddUserModalOpen = () => {
      setAddUserOpen(true);
   }

   const handleAddUserModalClose = () => {
      setActive(!active)
      setAddUserOpen(false);
      resetAddUserForm();
   };

   // Toggle edit mode in the Edit User modal
   const toggleEditMode = () => {
      setActive(!active);
   };

   // Reset all form values for Add User modal
   const resetAddUserForm = () => {
      setGroups([]);
      setGroupsPayload([]);
      setUserInput('');
      setPermission([]);
      setSendgridPermission([]);
      setMediaRelationsPermission([]);
      setCompaniesPermission([]);
      setDashboardPermission([]);
      setTwitterXPermission([]);
      setEntitlementPermission([]);
      setIntercomPermission([]);
      setToolsPermission([]);
      setAuth0Permission([]);
      setScriptRunnerv2Permission([]);
   };

   // Handles input from search bar.
   const handleInputChange = (e) => {
      setUserInput(e.target.value);
   };

   // Initial load of users - with debounce to prevent multiple rapid calls
   useEffect(() => {
      // Debounce rapid state changes to prevent multiple API calls
      const timer = setTimeout(async () => {
         setIsLoading(true);
         let sortField = orderBy;
         // Map firstName/lastName to email since they don't exist in DB
         if (orderBy === 'firstName' || orderBy === 'lastName') {
            sortField = 'email';
         }

         const result = await fetchAllUserProfiles(
            PaginationUtils.toBackendPage(page),
            rowsPerPage,
            debouncedSearchTerm,
            sortField,
            order
         );

         if (result.success) {
            const totalCount = result.pagination?.totalCount || 0;
            setUsers(result.data);
            setTotalUsers(totalCount);

            // Ensure current page is valid after data update
            if (!PaginationUtils.isValidPage(page, totalCount, rowsPerPage)) {
               const safePage = PaginationUtils.getSafePage(page, totalCount, rowsPerPage);
               if (safePage !== page) {
                  setPage(safePage);
               }
            }
         } else {
            setFeedbackMsg(result.error);
            setSeverity('error');
         }
         setIsLoading(false);
      }, PAGINATION_CONFIG.API_DEBOUNCE_DELAY);

      return () => {
         clearTimeout(timer);
      };
   }, [page, rowsPerPage, debouncedSearchTerm, order, orderBy]);

   // Load GSD groups from permission_groups collection via API
   useEffect(() => {
      async function fetchGroups() {
         try {
            const response = await AdminService.getPermissionGroups();
            if (response.data && response.data.groups) {
               const groupNames = response.data.groups.map(group => group.displayName || group.name);
               setGSDGroups(groupNames);
            } else {
               logger.error('Error fetching permission groups: Invalid response format');
            }
         } catch (error) {
            logger.error('Error fetching permission groups:', error);
         }
      }

      fetchGroups();
   }, []);

   // Extract lastActive data from users array
   useEffect(() => {
      function processLastActiveData() {
         if (users.length > 0) {
            const lastActiveMap = {};
            users.forEach(user => {
               if (user.email && user.lastActive) {
                  lastActiveMap[user.email] = user.lastActive;
               }
            });
         }
      }

      processLastActiveData();
   }, [users]);

   const handleFetchUserProfile = async (email) => {
      try {
         setIsLoading(true);
         const result = await fetchUserProfile(email);
         if (result.success) {
            const userData = result.data;
            logger.debug('User profile data:', userData);
            if (userData.profilePictureKey || userData.profilePictureUrl) {
               userData.profilePicture = userData.profilePictureUrl || true; // Use URL if available, or true to indicate it exists
            }
            setProfileData(userData);
            setGroups(userData.groups || []);
            setGroupsPayload(userData.groups || []);
            // Initialize edit permissions from user data
            setEditPermission(userData.permissions || []);
            setEditSendgridPermission(userData.sendgridpermissions || []);
            setEditMediaRelationsPermission(userData.mediarelationspermissions || []);
            setEditCompaniesPermission(userData.companiespermissions || []);
            setEditDashboardPermission(userData.dashboardpermissions || []);
            setEditTwitterXPermission(userData.twitterxpermissions || []);
            setEditEntitlementPermission(userData.entitlementpermissions || []);
            setEditIntercomPermission(userData.intercompermissions || []);
            setEditToolsPermission(userData.toolspermissions || []);
            setEditAuth0Permission(userData.auth0permissions || []);
            setEditScriptRunnerv2Permission(userData.scriptRunnerv2permissions || []);
            // Initialize as not in edit mode
            setActive(false);
            setGetUserOpen(true);
         } else {
            setOpenSnackBar(true);
            setFeedbackMsg(result.error);
            setSeverity('error');
         }
      } finally {
         setIsLoading(false);
      }
   };

   // Refresh profile data without changing modal state
   const refreshProfileData = async (email) => {
      try {
         const result = await fetchUserProfile(email);
         if (result.success) {
            const userData = result.data;
            if (userData.profilePictureKey || userData.profilePictureUrl) {
               userData.profilePicture = userData.profilePictureUrl || true;
            }
            setProfileData(userData);
            // Update edit permissions
            setEditPermission(userData.permissions || []);
            setEditSendgridPermission(userData.sendgridpermissions || []);
            setEditMediaRelationsPermission(userData.mediarelationspermissions || []);
            setEditCompaniesPermission(userData.companiespermissions || []);
            setEditDashboardPermission(userData.dashboardpermissions || []);
            setEditTwitterXPermission(userData.twitterxpermissions || []);
            setEditEntitlementPermission(userData.entitlementpermissions || []);
            setEditIntercomPermission(userData.intercompermissions || []);
            setEditToolsPermission(userData.toolspermissions || []);
            setEditAuth0Permission(userData.auth0permissions || []);
            setEditScriptRunnerv2Permission(userData.scriptRunnerv2permissions || []);
            // Update groups
            setGroups(userData.groups || []);
            setGroupsPayload(userData.groups || []);
         }
      } catch (error) {
         logger.error('Error refreshing profile data:', error);
      }
   };

   const handleNewUserSubmit = async () => {
      try {
         setIsLoading(true);
         const permissionsObj = {
            email: userInput,
            username: userInput,
            groups: groupsPayload,
            permissions: permission,
            sendgridpermissions: sendgridPermission,
            mediarelationspermissions: mediaRelationsPermission,
            companiespermissions: companiesPermission,
            dashboardpermissions: dashboardPermission,
            twitterxpermissions: twitterXPermission,
            entitlementpermissions: entitlementPermission,
            intercompermissions: intercomPermission,
            toolspermissions: toolsPermission,
            auth0permissions: auth0Permission,
            scriptRunnerv2permissions: scriptRunnerv2Permission
         };

         const result = await addNewUser(userInput, permissionsObj);
         if (result.success) {
            setOpenSnackBar(true);
            setSeverity('success');
            setFeedbackMsg(result.message);
            setAddUserOpen(false);
            resetAddUserForm();
            // Removed refreshTable() - useEffect will handle the refresh automatically
            setPage(0);
         } else {
            setOpenSnackBar(true);
            setFeedbackMsg(result.error);
            setSeverity('error');
         }
      } finally {
         setIsLoading(false);
      }
   };

   const handleUpdateProfile = async () => {
      try {
         setIsLoading(true);
         // Include permissions in the update payload
         const updatePayload = {
            ...profileData,
            groups: groupsPayload,
            permissions: editPermission,
            sendgridpermissions: editSendgridPermission,
            mediarelationspermissions: editMediaRelationsPermission,
            companiespermissions: editCompaniesPermission,
            dashboardpermissions: editDashboardPermission,
            twitterxpermissions: editTwitterXPermission,
            entitlementpermissions: editEntitlementPermission,
            intercompermissions: editIntercomPermission,
            toolspermissions: editToolsPermission,
            auth0permissions: editAuth0Permission,
            scriptRunnerv2permissions: editScriptRunnerv2Permission
         };
         const result = await updateUserProfile(updatePayload, groupsPayload);
         if (result.success) {
            // Refresh profile data from server to get latest state
            await refreshProfileData(profileData.email);

            setOpenSnackBar(true);
            setSeverity('success');
            setFeedbackMsg(result.message);

            // Keep the modal open but switch back to view mode
            toggleEditMode();
            // Disable loading state
            setActive(false);
         } else {
            setOpenSnackBar(true);
            setFeedbackMsg(result.error);
            setSeverity('error');
         }
      } finally {
         setIsLoading(false);
      }
   };

   // Handle groups checkbox changes
   const groupsPermissionChangeHandler = (event) => {
      let val = event.target.value;
      const arr = [...groups];  // Create a copy to avoid direct state mutation

      if (event.target.checked) {
         // Check if already exists
         if (arr.indexOf(val) === -1) {
            arr.push(val);
         }
      } else {
         const index = arr.indexOf(val);
         if (index !== -1) {
            arr.splice(index, 1);
         }
      }

      setGroups(arr);
      setGroupsPayload(arr);
   };

   const handlePermissionChange = createPermissionHandler(permission, setPermission, (arr) => { });
   const handleDashboardPermissionChange = createPermissionHandler(dashboardPermission, setDashboardPermission, (arr) => { });
   const handleSendgridPermissionChange = createPermissionHandler(sendgridPermission, setSendgridPermission, (arr) => { });
   const handleMediaRelationsPermissionChange = createPermissionHandler(mediaRelationsPermission, setMediaRelationsPermission, (arr) => { });
   const handleCompaniesPermissionChange = createPermissionHandler(companiesPermission, setCompaniesPermission, (arr) => { });
   const handleTwitterXPermissionChange = createPermissionHandler(twitterXPermission, setTwitterXPermission, (arr) => { });
   const handleEntitlementPermissionChange = createPermissionHandler(entitlementPermission, setEntitlementPermission, (arr) => { });
   const handleIntercomPermissionChange = createPermissionHandler(intercomPermission, setIntercomPermission, (arr) => { });
   const handleToolsPermissionChange = createPermissionHandler(toolsPermission, setToolsPermission, (arr) => { });
   const handleAuth0PermissionChange = createPermissionHandler(auth0Permission, setAuth0Permission, (arr) => { });
   const handleScriptRunnerv2PermissionChange = createPermissionHandler(scriptRunnerv2Permission, setScriptRunnerv2Permission, (arr) => { });

   // Edit modal permission handlers
   const handleEditPermissionChange = createPermissionHandler(editPermission, setEditPermission, (arr) => { });
   const handleEditDashboardPermissionChange = createPermissionHandler(editDashboardPermission, setEditDashboardPermission, (arr) => { });
   const handleEditSendgridPermissionChange = createPermissionHandler(editSendgridPermission, setEditSendgridPermission, (arr) => { });
   const handleEditMediaRelationsPermissionChange = createPermissionHandler(editMediaRelationsPermission, setEditMediaRelationsPermission, (arr) => { });
   const handleEditCompaniesPermissionChange = createPermissionHandler(editCompaniesPermission, setEditCompaniesPermission, (arr) => { });
   const handleEditTwitterXPermissionChange = createPermissionHandler(editTwitterXPermission, setEditTwitterXPermission, (arr) => { });
   const handleEditEntitlementPermissionChange = createPermissionHandler(editEntitlementPermission, setEditEntitlementPermission, (arr) => { });
   const handleEditIntercomPermissionChange = createPermissionHandler(editIntercomPermission, setEditIntercomPermission, (arr) => { });
   const handleEditToolsPermissionChange = createPermissionHandler(editToolsPermission, setEditToolsPermission, (arr) => { });
   const handleEditAuth0PermissionChange = createPermissionHandler(editAuth0Permission, setEditAuth0Permission, (arr) => { });
   const handleEditScriptRunnerv2PermissionChange = createPermissionHandler(editScriptRunnerv2Permission, setEditScriptRunnerv2Permission, (arr) => { });

   const handleCloseSnackBar = () => {
      setOpenSnackBar(false);
   };

   const handleConfirmDelete = (email) => {
      setUserToDeleteWithOptions(email);
      setDeleteOptionsOpen(true);
   };

   const handleDeleteWithOptionsConfirmed = async () => {
      try {
         setDeleteOptionsOpen(false);
         setIsLoading(true);

         let result;
         if (deleteType === "soft") {
            result = await deleteUserProfile(userToDeleteWithOptions);
         } else {
            result = await hardDeleteUserProfile(userToDeleteWithOptions);
         }

         if (result.success) {
            setOpenSnackBar(true);
            setSeverity('success');
            setFeedbackMsg(result.message);
         } else {
            setOpenSnackBar(true);
            setFeedbackMsg(result.error);
            setSeverity('error');
         }
      } finally {
         setIsLoading(false);
         setUserToDeleteWithOptions(null);
         setGetUserOpen(false);
      }
   };

   const handleDeleteConfirmed = async () => {
      try {
         setConfirmDialogOpen(false);
         setIsLoading(true);

         const result = await deleteUserProfile(userToDelete);
         if (result.success) {
            setOpenSnackBar(true);
            setSeverity('success');
            setFeedbackMsg(result.message);
         } else {
            setOpenSnackBar(true);
            setFeedbackMsg(result.error);
            setSeverity('error');
         }
      } finally {
         setIsLoading(false);
         setUserToDelete(null);
         setGetUserOpen(false);
      }
   };

   const handleExportUsers = async () => {
      try {
         setIsLoading(true);
         const exportOptions = {
            fieldGroups: {
               identityFields: ['_id', 'firstName', 'lastName', 'preferredName', 'email'],
               groupsField: ['groups'],
               permissionFields: [
                  'permissions',
                  'sendgridpermissions',
                  'dashboardpermissions',
                  'twitterxpermissions',
                  'entitlementpermissions',
                  'intercompermissions',
                  'toolspermissions',
                  'mediarelationspermissions',
                  'companiespermissions'
               ],
               activityFields: ['lastLogin']
            },
            restrictToFieldGroups: true,
            filename: `gsd-users-export-${new Date().toISOString().split('T')[0]}.csv`
         };

         const result = await exportUsers(exportOptions);

         if (result.success) {
            setOpenSnackBar(true);
            setSeverity('success');
            setFeedbackMsg(`${result.count || ''} users exported successfully!`);
         } else {
            setOpenSnackBar(true);
            setFeedbackMsg(result.error);
            setSeverity('error');
         }
      } finally {
         setIsLoading(false);
      }
   };

   const handleDeleteFieldConfirm = (email, field, label) => {
      setFieldToDelete({ email, field, label });
      setDeleteFieldConfirmOpen(true);
   };

   const handleDeleteField = async () => {
      setDeleteProfileFieldLoading(true);
      try {
         // Pass the resetBirthdayTimer flag if the field is birthday
         const resetTimer = fieldToDelete.field === 'birthday' ? resetBirthdayTimer : false;
         const result = await deleteUserProfileField(fieldToDelete.email, fieldToDelete.field, resetTimer);
         if (result.success) {
            // Refresh profile data from server to get latest state
            await refreshProfileData(fieldToDelete.email);

            // Show success message
            setFeedbackMsg(result.message);
            setSeverity('success');
            setOpenSnackBar(true);
         } else {
            setFeedbackMsg(result.error);
            setSeverity('error');
            setOpenSnackBar(true);
         }
      } catch (error) {
         setFeedbackMsg(`Error deleting ${fieldToDelete.label}: ${error.message}`);
         setSeverity('error');
         setOpenSnackBar(true);
      } finally {
         setDeleteProfileFieldLoading(false);
         setDeleteFieldConfirmOpen(false);
         setFieldToDelete({ email: '', field: '', label: '' });
         setResetBirthdayTimer(false); // Reset the checkbox state
      }
   };

   // Handle confirmation to reset birthday timer
   const handleResetBirthdayTimerConfirm = (email) => {
      setUserForBirthdayTimerReset(email);
      setResetBirthdayTimerConfirmOpen(true);
   };

   // Reset birthday timer independently of the birthday field status
   const handleResetBirthdayTimer = async () => {
      setResetBirthdayTimerLoading(true);
      try {
         const result = await resetUserBirthdayTimer(userForBirthdayTimerReset);
         if (result.success) {
            // Refresh profile data from server to get latest state
            await refreshProfileData(userForBirthdayTimerReset);

            // Show success message
            setFeedbackMsg(result.message);
            setSeverity('success');
            setOpenSnackBar(true);
         } else {
            // Show error message
            setFeedbackMsg(result.error || 'Failed to reset birthday timer');
            setSeverity('error');
            setOpenSnackBar(true);
         }
      } catch (error) {
         setFeedbackMsg(`Error resetting birthday timer: ${error.message}`);
         setSeverity('error');
         setOpenSnackBar(true);
      } finally {
         setResetBirthdayTimerLoading(false);
         setResetBirthdayTimerConfirmOpen(false);
         setUserForBirthdayTimerReset(null);
      }
   };

   return (
      <div className={styles.container}>
         <Container maxWidth="lg">
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
               <Tabs
                  value={selectedTab}
                  onChange={handleTabChange}
                  aria-label="admin tabs"
                  sx={{
                     '& .MuiTabs-indicator': {
                        backgroundColor: '#1D9F9F',
                     },
                     '& .MuiTab-root': {
                        color: '#666',
                        '&.Mui-selected': {
                           color: '#1D9F9F',
                           fontWeight: 500
                        },
                        '&:hover': {
                           color: '#1D9F9F',
                           opacity: 0.7
                        }
                     }
                  }}
               >
                  <Tab label="Users" />
                  <Tab label="Permission Groups" />
               </Tabs>
            </Box>

            {selectedTab === 0 ? (
               <Paper elevation={3} className={styles.tableContainer}>
                  <TableContainer sx={{ marginTop: 1 }}>
                     <AdminHeader
                        searchValue={searchedVal}
                        onSearchChange={handleSearchChange}
                        onAddUser={handleAddUserModalOpen}
                        onExportUsers={handleExportUsers}
                        isLoading={isLoading}
                     />

                     <UserManagementTable
                        users={users}
                        isLoading={isLoading}
                        page={page}
                        rowsPerPage={rowsPerPage}
                        totalUsers={totalUsers}
                        order={order}
                        orderBy={orderBy}
                        onRequestSort={handleRequestSort}
                        onChangePage={handleChangePage}
                        onChangeRowsPerPage={handleChangeRowsPerPage}
                        onEditUser={handleFetchUserProfile}
                        onDeleteUser={handleConfirmDelete}
                        onBlockUser={handleBlockConfirmOpen}
                     />
                  </TableContainer>
               </Paper>
            ) : (
               <PermissionGroups />
            )}

            <AddUserModal
               open={openAddUserModal}
               onClose={handleAddUserModalClose}
               userInput={userInput}
               onInputChange={handleInputChange}
               groups={groups}
               GSDgroups={GSDgroups}
               onGroupsChange={groupsPermissionChangeHandler}
               permission={permission}
               onPermissionChange={handlePermissionChange}
               sendgridPermission={sendgridPermission}
               onSendgridPermissionChange={handleSendgridPermissionChange}
               mediaRelationsPermission={mediaRelationsPermission}
               onMediaRelationsPermissionChange={handleMediaRelationsPermissionChange}
               companiesPermission={companiesPermission}
               onCompaniesPermissionChange={handleCompaniesPermissionChange}
               dashboardPermission={dashboardPermission}
               onDashboardPermissionChange={handleDashboardPermissionChange}
               twitterXPermission={twitterXPermission}
               onTwitterXPermissionChange={handleTwitterXPermissionChange}
               entitlementPermission={entitlementPermission}
               onEntitlementPermissionChange={handleEntitlementPermissionChange}
               intercomPermission={intercomPermission}
               onIntercomPermissionChange={handleIntercomPermissionChange}
               toolsPermission={toolsPermission}
               onToolsPermissionChange={handleToolsPermissionChange}
               auth0Permission={auth0Permission}
               onAuth0PermissionChange={handleAuth0PermissionChange}
               scriptRunnerv2Permission={scriptRunnerv2Permission}
               onScriptRunnerv2PermissionChange={handleScriptRunnerv2PermissionChange}
               isLoading={isLoading}
               onSubmit={handleNewUserSubmit}
            />

            <EditUserModal
               open={openGetUserModal}
               onClose={handleGetUserModalClose}
               profileData={profileData}
               groups={groups}
               GSDgroups={GSDgroups}
               onGroupsChange={groupsPermissionChangeHandler}
               permission={editPermission}
               onPermissionChange={handleEditPermissionChange}
               sendgridPermission={editSendgridPermission}
               onSendgridPermissionChange={handleEditSendgridPermissionChange}
               mediaRelationsPermission={editMediaRelationsPermission}
               onMediaRelationsPermissionChange={handleEditMediaRelationsPermissionChange}
               companiesPermission={editCompaniesPermission}
               onCompaniesPermissionChange={handleEditCompaniesPermissionChange}
               dashboardPermission={editDashboardPermission}
               onDashboardPermissionChange={handleEditDashboardPermissionChange}
               twitterXPermission={editTwitterXPermission}
               onTwitterXPermissionChange={handleEditTwitterXPermissionChange}
               entitlementPermission={editEntitlementPermission}
               onEntitlementPermissionChange={handleEditEntitlementPermissionChange}
               intercomPermission={editIntercomPermission}
               onIntercomPermissionChange={handleEditIntercomPermissionChange}
               toolsPermission={editToolsPermission}
               onToolsPermissionChange={handleEditToolsPermissionChange}
               auth0Permission={editAuth0Permission}
               onAuth0PermissionChange={handleEditAuth0PermissionChange}
               scriptRunnerv2Permission={editScriptRunnerv2Permission}
               onScriptRunnerv2PermissionChange={handleEditScriptRunnerv2PermissionChange}
               active={active}
               onToggleEditMode={toggleEditMode}
               onUpdateProfile={handleUpdateProfile}
               onDeleteUser={handleConfirmDelete}
               onBlockUser={handleBlockConfirmOpen}
               onDeleteField={handleDeleteFieldConfirm}
               onResetBirthdayTimer={handleResetBirthdayTimerConfirm}
               isLoading={isLoading}
            />

            <ConfirmationDialogs
               // Basic delete user dialog
               deleteConfirmOpen={confirmDialogOpen}
               userToDelete={userToDelete}
               onDeleteConfirmed={handleDeleteConfirmed}
               onDeleteClose={() => setConfirmDialogOpen(false)}

               // Delete options dialog
               deleteOptionsOpen={deleteOptionsOpen}
               onDeleteOptionsClose={() => setDeleteOptionsOpen(false)}
               deleteType={deleteType}
               onDeleteTypeChange={setDeleteType}
               userToDeleteWithOptions={userToDeleteWithOptions}
               onDeleteWithOptionsConfirmed={handleDeleteWithOptionsConfirmed}

               // Block confirmation dialog
               blockConfirmOpen={blockConfirmOpen}
               onBlockConfirmClose={() => setBlockConfirmOpen(false)}
               userToToggleBlock={userToToggleBlock}
               blockAction={blockAction}
               onBlockConfirmed={handleBlockConfirmed}

               // Delete field confirmation dialog
               deleteFieldConfirmOpen={deleteFieldConfirmOpen}
               onDeleteFieldConfirmClose={() => setDeleteFieldConfirmOpen(false)}
               fieldToDelete={fieldToDelete}
               resetBirthdayTimer={resetBirthdayTimer}
               onResetBirthdayTimerChange={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setResetBirthdayTimer(e.target.checked);
               }}
               onDeleteField={handleDeleteField}
               deleteProfileFieldLoading={deleteProfileFieldLoading}

               // Reset birthday timer confirmation dialog
               resetBirthdayTimerConfirmOpen={resetBirthdayTimerConfirmOpen}
               onResetBirthdayTimerConfirmClose={() => setResetBirthdayTimerConfirmOpen(false)}
               userForBirthdayTimerReset={userForBirthdayTimerReset}
               onResetBirthdayTimerConfirmed={handleResetBirthdayTimer}
               resetBirthdayTimerLoading={resetBirthdayTimerLoading}

               isLoading={isLoading}
            />

            <Snackbar
               open={openSnackBar}
               autoHideDuration={4000}
               onClose={handleCloseSnackBar}
               anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
               <Alert onClose={handleCloseSnackBar} severity={severity} sx={{ width: '100%' }} variant="filled">
                  {feedbackMsg}
               </Alert>
            </Snackbar>
         </Container>
      </div>
   );
}

Admin.propTypes = {};

Admin.defaultProps = {};

export default Admin;
